import {
  forwardRef, memo, useRef, useCallback, useEffect, useState, useMemo,
  type ReactNode, type MouseEventHandler, type ChangeEvent
} from 'react';
import size from 'lodash/size';
import trim from 'lodash/trim';
import toString from 'lodash/toString';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
// Material Icon imports
import FileUploadRounded from '@mui/icons-material/FileUploadRounded';
// Skillmore UI Components
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import usePost from '@empathco/ui-components/src/hooks/usePost';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import PlusButton from '@empathco/ui-components/src/elements/PlusButton';
import InfoButton from '@empathco/ui-components/src/elements/InfoButton';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import ActionSucceededMessage from '@empathco/ui-components/src/elements/ActionSucceededMessage';
// local imports
import { User } from '../models/user';
import { MAX_UPLOAD_SIZE } from '../config/params';
import useCustomerSettings from '../config/customer';
import { axiosInstance, API_MY_RESUME_UPLOAD } from '../config/api';
import { getAuthHeaders } from '../helpers/storage';
// SCSS imports
import { header, subtitle, hiddenInput, lineBreakLg } from './BuilderSubheader.module.scss';

const subtitleValues = { br: <br/> };
const titleSx: SxProps<Theme> = { px: { sm: 4 }, pb: { xs: 1, sm: 0 }, display: 'flex', flexDirection: 'column' } as const;
const updatesSx: SxProps<Theme> = {
  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5,
  pl: { sm: 4 }, pb: 1.5 } as const;
const linkedinBtnSx: SxProps<Theme> = { ml: 1 } as const;

type BuilderSubheaderProps = {
  readonly user?: User | null;
  readonly title: string;
  readonly info: string;
  readonly infoLink?: string;
  readonly infoText?: string;
  readonly disabled?: boolean;
  readonly pending?: boolean | null;
  readonly pendingResume?: boolean | null;
  readonly pendingLinkedIn?: boolean | null;
  readonly onAdd?: MouseEventHandler<HTMLButtonElement>;
  readonly onLinkedIn?: (profileId: string) => void;
  readonly onUpload?: () => void;
  readonly onUploadStart?: () => void;
  readonly onUploadEnd?: () => void;
  // for Storybook only
  readonly testUploading?: boolean;
};

const BuilderSubheader = forwardRef<HTMLElement, BuilderSubheaderProps>(({
  user,
  title,
  info,
  infoLink,
  infoText,
  disabled = false,
  pending = false,
  pendingResume = false,
  pendingLinkedIn = false,
  onAdd,
  onLinkedIn,
  onUpload,
  onUploadStart,
  onUploadEnd,
  testUploading
  // eslint-disable-next-line complexity, max-lines-per-function
}, ref) => {
  const {
    // HAS_RESUME_UPLOADER, HAS_RESUME_LINKEDIN,
    PROFILE_BUILDER_VIDEO_URL, PROFILE_BUILDER_MANUAL_URL
  } = useCustomerSettings();

  const fileInput = useRef<HTMLInputElement>(null);

  const [err, setErr] = useState(false);
  const [profileId, setProfileId] = useState('');

  const { post, loading, failed, succeeded } = usePost(axiosInstance, getAuthHeaders);
  const uploading = loading || testUploading;

  const hasUpload = Boolean(/* HAS_RESUME_UPLOADER && */ onUpload);
  const hasLinkedIn = Boolean(/* HAS_RESUME_LINKEDIN && */ onLinkedIn);

  const titleValues = useMemo(() => ({
    company: <FormattedMessage id="header.brand"/>,
    org: user?.org?.title || null,
    has_skills: Boolean(user?.has_current_job_skills || user?.has_in_demand_skills)
  }), [user]);

  const helperLinkValues = useMemo(() => PROFILE_BUILDER_VIDEO_URL || PROFILE_BUILDER_MANUAL_URL ? {
    video: Boolean(PROFILE_BUILDER_VIDEO_URL),
    // eslint-disable-next-line react/no-unstable-nested-components
    link: (chunks?: ReactNode | null): ReactNode => (
      <StandardLink href={PROFILE_BUILDER_VIDEO_URL || PROFILE_BUILDER_MANUAL_URL}>
        {mapChunks(chunks)}
      </StandardLink>
    )
  } : undefined, [PROFILE_BUILDER_VIDEO_URL, PROFILE_BUILDER_MANUAL_URL]);

  const slotProps = useMemo(() => hasLinkedIn ? {
    input: {
      startAdornment: (
        <InputAdornment position="start">
          <FormattedMessage id="builder.resume.linkedin.adornment"/>
        </InputAdornment>
      )
    }
  } : undefined, [hasLinkedIn]);

  const handleProfileIdChange = useCallback((event: ChangeEvent<{ name?: string; value: unknown; }>) => {
    event?.preventDefault();
    setProfileId(toString(event?.target?.value));
  }, []);

  const handleFileInput = useCallback(() => {
    if (hasUpload) {
      setErr(false);
      fileInput.current?.click();
    }
  }, [hasUpload]);

  const handleChange = useCallback((event?: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event?.target?.files?.[0];
    if (hasUpload && selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedFile.size > MAX_UPLOAD_SIZE || (
        selectedFile.type !== 'application/pdf' && selectedFile.type !== 'text/plain'
      )) {
        if (fileInput.current) fileInput.current.value = '';
        setErr(true);
        return;
      }
      setErr(false);
      onUploadStart?.();
      post(API_MY_RESUME_UPLOAD, formData);
    }
  }, [hasUpload, post, onUploadStart]);

  const handleOK = useCallback(() => {
    // no-op
  }, []);

  const handleLinkedIn = useCallback(() => {
    onLinkedIn?.(profileId);
  }, [profileId, onLinkedIn]);

  useEffect(() => {
    if (hasUpload && fileInput.current && !loading && (failed || succeeded)) {
      fileInput.current.value = '';
      onUploadEnd?.();
    }
  }, [hasUpload, loading, failed, succeeded, onUploadEnd]);

  useEffect(() => {
    if (hasUpload && succeeded) onUpload?.();
  }, [hasUpload, succeeded, onUpload]);

  const addButton = onAdd ? (
    <PlusButton
        label="builder.add_skill"
        disabled={disabled || pending || loading || uploading}
        onClick={onAdd}
    />
  ) : undefined;

  return (
    <>
      <CardHeader
          ref={ref}
          disableTypography
          title={(
            <Box sx={titleSx}>
              <BoxTypography pb={1} variant="h4">
                <FormattedMessage id={title} defaultMessage={title} values={titleValues}/>
                {infoText ? (
                  <>
                    {' '}
                    <InfoButton
                        textVariant="subtitle2"
                        text={infoLink}
                        help={infoText}
                    />
                  </>
                ) : undefined}
              </BoxTypography>
              <Box className={subtitle}>
                <FormattedMessage id={info} defaultMessage={info} values={subtitleValues}/>
              </Box>
              {helperLinkValues ? (
                <Box pt={1} className={subtitle}>
                  <FormattedMessage id="builder.resume.helper_link" values={helperLinkValues}/>
                </Box>
              ) : undefined}
            </Box>
          )}
          action={hasUpload || hasLinkedIn ? undefined : addButton}
          className={header}
      />
      {hasUpload || hasLinkedIn ? (
        <>
          <CardSection compact>
            <Box sx={updatesSx}>
              <BoxTypography variant="subtitle2" pr={1.5} py={1.5}>
                <FormattedMessage id="builder.resume"/>
              </BoxTypography>
              {addButton ? (
                <Box display="flex" alignItems="center" px={4}>
                  {addButton}
                </Box>
              ) : undefined}
              {hasUpload ? (
                <>
                  {addButton && hasLinkedIn ? <Box width="100%"/> : undefined}
                  <Box display="flex" alignItems="center">
                    {addButton ? (
                      <BoxTypography variant="subtitle2" pr={addButton && !hasLinkedIn ? 4 : 1.5} py={1.5}>
                        <FormattedMessage id="builder.resume.or"/>
                      </BoxTypography>
                    ) : undefined}
                    <Button
                        color="secondary"
                        variant="text"
                        disabled={disabled || pending || uploading || pendingResume || pendingLinkedIn ? true : undefined}
                        onClick={handleFileInput}
                        startIcon={uploading || (pendingResume && !pending)
                          ? <CircularProgress size={26} color={pendingResume ? 'inherit' : 'secondary'}/>
                          : <FileUploadRounded fontSize="inherit" color="inherit"/>}
                    >
                      <FormattedMessage
                          id={(pendingResume && !pending && 'builder.resume.processing') ||
                            (uploading && 'builder.resume.progress') ||
                            'builder.resume.button'}
                      />
                    </Button>
                    <input
                        ref={fileInput}
                        type="file"
                        accept=".txt,.pdf,text/plain,application/pdf"
                        onChange={handleChange}
                        className={hiddenInput}
                    />
                    <InfoButton
                        small
                        help="builder.resume.supported_files"
                        placement="bottom-end"
                    />
                  </Box>
                </>
              ) : undefined}
              {hasLinkedIn ? (
                <>
                  {addButton || hasUpload ? <Box width="100%" className={lineBreakLg}/> : undefined}
                  <Box display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                    <BoxTypography variant="subtitle2" px={1.5} py={1.5}>
                      {addButton || hasUpload ? (
                        <>
                          <FormattedMessage id="builder.resume.or"/>
                          &nbsp;
                        </>
                      ) : undefined}
                      <FormattedMessage id="builder.resume.linkedin.label"/>
                    </BoxTypography>
                    <TextField
                        id="linkedin-profile-id"
                        variant="outlined"
                        margin="dense"
                        size="small"
                        slotProps={slotProps}
                        value={profileId}
                        onChange={handleProfileIdChange}
                        disabled={disabled || pending || uploading || pendingResume || pendingLinkedIn ? true : undefined}
                    />
                    <Button
                        color="secondary"
                        variant="text"
                        onClick={handleLinkedIn}
                        disabled={disabled || pending || uploading || pendingResume || pendingLinkedIn ||
                          size(trim(profileId)) < 3}
                        startIcon={pendingLinkedIn ? <CircularProgress size={26} color="inherit"/> : undefined}
                        sx={linkedinBtnSx}
                    >
                      <FormattedMessage id={pendingLinkedIn ? 'builder.linkedin.processing' : 'common.button.submit'}/>
                    </Button>
                  </Box>
                </>
              ) : undefined}
            </Box>
          </CardSection>
          {err ? (
            <ActionSnackbar
                severity="error"
                open
                message="builder.resume.unsupported_file"
            />
          ) : undefined}
          <ActionSnackbar
              severity="error"
              open={failed}
              message="builder.resume.error"
          />
          <ActionSucceededMessage
              open={succeeded}
              title="builder.resume.success.title"
              message="builder.resume.success.message"
              onOK={handleOK}
              compact
          />
        </>
      ) : undefined}
      {(hasUpload || hasLinkedIn) && (((pendingResume || pendingLinkedIn) && !pending) || uploading) ? undefined : <Divider/>}
    </>
  );
});

BuilderSubheader.displayName = 'BuilderSubheader';

export default memo(BuilderSubheader);
